<template>
	<ui-dropdown
		@opened="$emit('opened')"
		@close="$emit('close')"
		:dropdown-class="
			(alignment === 'right' ? 'right-0' : 'left-0') +
			' ' +
			(verticalAlignment === 'bottom' ? 'bottom-12' : 'top-12') +
			' origin-top-right absolute mt-0 w-full min-w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden'
		"
		:button-class="[
			'px-3 py-2 rounded-md shadow-sm whitespace-nowrap font-medium flex items-center w-full border focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ' + (size === 'sm' ? 'text-sm' : 'text-base'),
			markInvalid
				? 'border-red-600 bg-red-100 text-gray-400'
				: disabled
				? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
				: 'border-gray-300 bg-white text-gray-400 focus:text-orange-500',
		].join(' ')"
		:disabled="disabled"
	>
		<template v-slot:button>
			<ui-icon
				v-if="icon"
				class="h-5 w-5 mr-3"
				:class="[markInvalid ? 'text-red-600' : 'text-gray-400']"
				name="calendar"
				stroke-width="2"
			/>
			<span class="flex-1 text-left" :class="markInvalid ? 'text-red-600' : 'text-gray-700'">{{ displayVal }}</span>
			<button
				v-if="clearable && val"
				type="button"
				class="ml-1 text-gray-400 hover:text-gray-600"
				@click.stop="clearValue"
			>
				<font-awesome-icon :icon="['fas', 'times']" class="h-3 w-3" />
			</button>
			<font-awesome-icon
				v-else-if="chevron"
				:icon="['fas', 'chevron-down']"
				class="ml-2 flex-shrink-0 h-3 w-3 text-gray-700"
			/>
		</template>

		<!-- picker overlay -->
		<template v-slot:items>
			<div class="flex justify-between border-b border-gray-300" @click.stop="">
				<button
					class="m-2 focus:ring-2 focus:ring-orange-500 focus:text-orange-500 rounded-sm cursor-pointer"
					:class="[!canSelect(picker.year - 1) ? 'text-gray-300 cursor-not-allowed' : '']"
					:disabled="!canSelect(picker.year - 1)"
					@click.prevent="picker.year--"
				>
					<ui-icon class="h-5 w-5" name="chevron-left" stroke-width="2" />
				</button>

				<p class="m-2 text-gray-700 font-medium">{{ picker.year }}</p>

				<button
					class="m-2 focus:ring-2 focus:ring-orange-500 focus:text-orange-500 rounded-sm cursor-pointer"
					:class="[!canSelect(picker.year + 1) ? 'text-gray-300 cursor-not-allowed' : '']"
					:disabled="!canSelect(picker.year + 1)"
					@click.prevent="picker.year++"
				>
					<ui-icon class="h-5 w-5" name="chevron-right" stroke-width="2" />
				</button>
			</div>

			<div class="grid grid-cols-4 grid-rows-3">
				<button
					v-for="(month, idx) in monthLabels"
					:key="idx"
					:class="[
						'px-3 py-2 focus:bg-orange-500 focus:text-white',
						isSelected(picker.year, idx + 1)
							? markInvalid ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
							: canSelect(picker.year, idx + 1)
							? 'hover:bg-gray-100'
							: 'text-gray-300 cursor-not-allowed',
					]"
					:disabled="!canSelect(picker.year, idx + 1)"
					@click.prevent="canSelect(picker.year, idx + 1) ? (picker.month = idx + 1) : null"
				>
					{{ month }}
				</button>
			</div>
		</template>
	</ui-dropdown>
</template>

<script>
export default {
	props: {
		value: {
			required: false,
			type: String,
		},
		autocomplete: {
			type: String,
			default: "on",
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		clearable: {
			type: Boolean,
			default: false,
		},
		/**
		 * Dropdown-Pfeil rechts wie bei den Filter-Dropdowns; weicht dem Clear-Button, sobald ein Wert gesetzt ist.
		 */
		chevron: {
			type: Boolean,
			default: false,
		},
		size: {
			type: String,
			default: 'base',
		},
		placeholder: {
			type: String,
			default: null,
		},
		markInvalid: {
			type: Boolean,
			default: false,
		},
		inputId: {
			type: String,
		},
		minDate: {
			type: String,
			default: "0000-00-01",
		},
		maxDate: {
			type: String,
			default: "9999-00-01",
		},
		icon: {
			type: Boolean,
			default: true,
		},
		alignment: {
			type: String,
			default: "left",
		},
		verticalAlignment: {
			type: String,
			default: "top",
		},
	},
	data() {
		return {
			val: this.value,
			picker: Vue.observable({
				year:  this.value ? this.value.substr(0, 4) * 1 : new Date().getFullYear(),
				month: this.value ? this.value.substr(5, 2) * 1 : null,
			}),
		};
	},
	watch: {
		/**
		 * Meldet Änderungen der Auswahl (`picker`) nach außen.
		 *
		 * `input` wird immer emittiert (v-model-Vertrag), `change` nur bei einer echten
		 * Nutzerauswahl. Wurde der Watcher durch einen Prop-Sync ausgelöst (der `value`-Watcher
		 * hat `picker` gesetzt und `_propSync` markiert), bleibt `change` aus — ein reines
		 * Prop-Update darf nicht als Nutzer-Änderung durchgehen (analog zu `InputGroup`).
		 *
		 * Das Flag wird immer ZUERST ausgelesen und zurückgesetzt — auch beim Early-Return für
		 * den Leer-Zustand (`month === null`, z.B. Prop-Sync auf `null`). Sonst bliebe es stehen
		 * und würde eine spätere echte Auswahl fälschlich stumm schalten.
		 *
		 * @param {{ year: number, month: number|null }} picker Aktueller Auswahlzustand
		 */
		picker: {
			deep: true,
			handler(picker) {
				const propSync = this._propSync;
				this._propSync = false;

				if (picker.month === null) return;

				this.val = this.monthToIsoDate(picker.year, picker.month);
				this.$emit("input", this.val);

				if (!propSync) {
					this.$emit("change");
				}
			},
		},
		/**
		 * Hält die interne Auswahl (`val` + `picker`) mit dem von außen gebundenen `value`-Prop
		 * synchron.
		 *
		 * Hintergrund: `val` und `picker` werden in `data()` nur EINMAL initial aus `value`
		 * abgeleitet. Ohne diesen Watcher kämen spätere Prop-Änderungen des Parents (asynchron
		 * nachgeladene Formulardaten, Reset, programmatisches Setzen) nicht mehr im Feld an.
		 *
		 * `_propSync` markiert die dadurch ausgelöste `picker`-Mutation als prop-getrieben,
		 * damit der `picker`-Watcher oben kein `change` feuert. Das Flag wird nur gesetzt, wenn
		 * `picker` tatsächlich mutiert — sonst liefe der `picker`-Watcher gar nicht, der es
		 * wieder konsumiert, und es bliebe fälschlich stehen.
		 *
		 * @param {string|null} v Neuer Wert des `value`-Props (ISO "YYYY-MM-01" oder null)
		 */
		value(v) {
			this.val = v;

			const year  = v ? v.substr(0, 4) * 1 : new Date().getFullYear();
			const month = v ? v.substr(5, 2) * 1 : null;

			if (this.picker.year !== year || this.picker.month !== month) {
				this._propSync = true;
			}

			if (this.picker.year !== year)   this.picker.year  = year;
			if (this.picker.month !== month) this.picker.month = month;
		},
	},
	methods: {
		/**
		 * @return {void}
		 */
		clearValue() {
			this.val          = null;
			this.picker.month = null;
			this.$emit('input', null);
			this.$emit('change');
		},

		/**
		 * @param {number} year
		 * @param {number} month
		 * @return {string}
		 */
		monthToIsoDate(year, month) {
			return [year, "-", month < 10 ? "0" : "", month, "-01"].join("");
		},

		/**
		 * @param {number} year
		 * @param {number|null} month
		 * @return {boolean}
		 */
		canSelect(year, month) {
			if (month) {
				const d = this.monthToIsoDate(year, month);
				return d >= this.minDate && d <= this.maxDate;
			}

			return true;
		},

		/**
		 * @param {number} year
		 * @param {number} month
		 * @return {boolean}
		 */
		isSelected(year, month) {
			return this.picker.year == year && this.picker.month == month;
		},
	},
	computed: {
		monthLabels() {
			return this.$cropOptions.monthLabels;
		},
		displayVal() {
			if (this.val) {
				return [this.monthLabels[this.picker.month - 1], " ", this.picker.year].join("");
			}

			return this.placeholder ?? this.__("Please select");
		},
	},
};
</script>
